import React from 'react';
import { Box, MenuItem, TextField, Tooltip } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { RbacRole, rolesV2Api } from '../../../../api/rolesV2Api';

interface Props {
  userId: number;
  /** The user's current v2 role code (row's `role_v2_code`, Default fallback). */
  currentRoleCode: string;
  /** Full v2 role catalog — passed down from the page/table fetch so this
   * cell never does its own per-row fetch (avoids an N+1 across the table). */
  roles: RbacRole[];
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
  /** External disable (e.g. the last-super-admin guard). */
  disabled?: boolean;
}

/**
 * Users-tab Role control (RBAC_V2). Exactly ONE role per user: a
 * single-select dropdown over the v2 role catalog, writing through
 * `rolesV2Api.setUserRole`. Replaces the legacy multi-chip role select;
 * every other Users-tab column/control is unchanged. Adapted from the
 * reference branch's mock `UserRoleSelectCell` — this version is bound to
 * `role_v2_code` already present on the row (no `getUserRole` fetch) and
 * writes real error codes (`last_super_admin`/`cannot_self_demote`/
 * `role_not_found`) instead of a generic failure message.
 */
const UserRoleSelectCell: React.FC<Props> = ({ userId, currentRoleCode, roles, onNotify, disabled }) => {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (roleCode: string) => rolesV2Api.setUserRole(userId, roleCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      // Holder counts on the Roles tab shift when an assignment changes.
      qc.invalidateQueries({ queryKey: ['rbac-v2', 'roles'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'last_super_admin') {
        onNotify('Cannot remove the last super-admin.', 'error');
      } else if (code === 'cannot_self_demote') {
        onNotify('You cannot remove your own super-admin role.', 'error');
      } else if (code === 'role_not_found') {
        onNotify('That role no longer exists.', 'error');
      } else {
        onNotify('Role update failed.', 'error');
      }
    },
  });

  return (
    <TextField
      size="small"
      select
      value={currentRoleCode}
      onChange={(e) => mutation.mutate(e.target.value)}
      disabled={disabled || mutation.isPending}
      SelectProps={{
        renderValue: (val) => {
          const r = roles.find((x) => x.role_code === val);
          return (
            <Tooltip title={r?.role_name ?? String(val)}>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  maxWidth: 168,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'middle',
                }}
              >
                {r?.role_name ?? String(val)}
              </Box>
            </Tooltip>
          );
        },
      }}
      sx={{ minWidth: 200 }}
    >
      {roles.map((r) => (
        <MenuItem key={r.role_code} value={r.role_code}>
          {r.role_name}
          {r.is_system ? ' (system)' : ''}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default UserRoleSelectCell;
